import { createApp, h } from "vue";
import { crmProviderNames, crmProviderTypes, type ICRMProviderType } from "~/utils/crm_types";

/**
 * A service to display modals in a style consistent with the application's UI.
 * This matches the modal style seen in the app's existing modals.
 */

/**
 * Shows a modal with the given title, message, and buttons.
 * The style matches the app's existing modals.
 */
export function showModal(options: { title: string; message: string; confirmText?: string; cancelText?: string; onConfirm?: () => void; onCancel?: () => void; isDanger?: boolean }) {
	// Create the modal element
	const modalContainer = document.createElement("div");
	modalContainer.className = "leadx-modal";
	document.body.appendChild(modalContainer);

	// Set default values
	const confirmText = options.confirmText || "Confirm";
	const cancelText = options.cancelText || "Cancel";
	const isDanger = options.isDanger || false;

	// Add background overlay
	const backgroundEl = document.createElement("div");
	backgroundEl.className = "leadx-modal-background";
	modalContainer.appendChild(backgroundEl);

	// Create the modal panel
	const modalPanel = document.createElement("div");
	modalPanel.className = "leadx-modal-panel panel shadow-large border-grey border-radius-normal";
	modalContainer.appendChild(modalPanel);

	const closeModal = () => {
		document.body.removeChild(modalContainer);
	};

	// Close on background click
	backgroundEl.addEventListener("click", () => {
		if (options.onCancel) options.onCancel();
		closeModal();
	});

	// Close button
	const closeBtn = document.createElement("button");
	closeBtn.className = "leadx-modal-close";
	closeBtn.type = "button";
	closeBtn.setAttribute("aria-label", "Close");
	closeBtn.innerHTML = `
		<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect x="0.221802" y="1.63604" width="2" height="20" rx="1" transform="rotate(-45 0.221802 1.63604)" fill="#000000" />
			<rect x="0.221802" y="14.364" width="20" height="2" rx="1" transform="rotate(-45 0.221802 14.364)" fill="#000000" />
		</svg>
	`;
	closeBtn.addEventListener("click", () => {
		if (options.onCancel) options.onCancel();
		closeModal();
	});
	modalPanel.appendChild(closeBtn);

	// Add content wrapper
	const contentEl = document.createElement("div");
	contentEl.className = "leadx-modal-content";
	modalPanel.appendChild(contentEl);

	// Add title
	const titleEl = document.createElement("h2");
	titleEl.className = "leadx-modal-title";
	titleEl.textContent = options.title;
	contentEl.appendChild(titleEl);

	// Add message
	const messageEl = document.createElement("p");
	messageEl.className = "leadx-modal-message";
	messageEl.textContent = options.message;
	contentEl.appendChild(messageEl);

	// Add actions container
	const actionsEl = document.createElement("div");
	actionsEl.className = "leadx-modal-actions";
	modalPanel.appendChild(actionsEl);

	// Add cancel button
	const cancelBtn = document.createElement("button");
	cancelBtn.className = "leadx-modal-button cancel";
	cancelBtn.textContent = cancelText;
	cancelBtn.style.background = "var(--grey-semi, #eeeff1)";
	cancelBtn.style.color = "#000000";
	cancelBtn.style.border = "none";
	cancelBtn.addEventListener("click", () => {
		if (options.onCancel) options.onCancel();
		closeModal();
	});
	actionsEl.appendChild(cancelBtn);

	// Add confirm button
	const confirmBtn = document.createElement("button");
	confirmBtn.className = `leadx-modal-button confirm${isDanger ? " danger" : ""}`;
	confirmBtn.textContent = confirmText;
	confirmBtn.style.background = "#be3437";
	confirmBtn.style.color = "#ffffff";
	confirmBtn.style.border = "1px solid #be3437";
	confirmBtn.style.border = "none";
	confirmBtn.addEventListener("click", () => {
		if (options.onConfirm) options.onConfirm();
		closeModal();
	});
	actionsEl.appendChild(confirmBtn);

	// Add styles
	if (!document.getElementById("leadx-modal-style")) {
		const style = document.createElement("style");
		style.id = "leadx-modal-style";
		style.textContent = `
		.leadx-modal {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 1000;
			padding: 20px;
		}

		.leadx-modal-background {
			position: absolute;
			inset: 0;
			background: rgba(240, 240, 240, 0.75);
			animation: leadx-modal-fade-in 0.3s ease;
		}

		.leadx-modal-panel {
			position: relative;
			z-index: 1001;
			width: 480px;
			max-width: 90vw;
			padding: 60px;
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: 20px;
			animation: leadx-modal-scale-in 0.3s ease;
		}

		.leadx-modal-close {
			position: absolute;
			top: 20px;
			right: 20px;
			width: 42px;
			height: 42px;
			border-radius: 50%;
			border: none;
			background: #eeeff1;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			transition: transform 0.15s ease, background-color 0.15s ease;
		}

		.leadx-modal-close:hover {
			background: #f8f8f8;
			transform: scale(1.02);
		}

		.leadx-modal-content {
			display: flex;
			flex-direction: column;
			gap: 10px;
		}

		.leadx-modal-title {
			margin: 0;
			font-size: 28px;
			font-weight: 600;
		}

		.leadx-modal-message {
			margin: 0;
			color: rgba(0, 0, 0, 0.6);
			line-height: 1.6;
		}

		.leadx-modal-actions {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: 10px;
			align-items: center;
		}

		.leadx-modal-button {
			height: 52px;
			width: 100%;
			max-width: 320px;
			border-radius: 26px;
			padding: 0 40px;
			font-size: 18px;
			font-weight: 700;
			letter-spacing: -0.2px;
			border: none;
			cursor: pointer;
			transition: transform 0.1s ease-out, box-shadow 0.1s ease-out;
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}

		.leadx-modal-button:active {
			transform: scale(0.99);
		}

		.leadx-modal-button.cancel {
			background: #eeeff1 !important;
			color: #000 !important;
		}

		.leadx-modal-button.confirm {
			background: #be3437 !important;
			color: #fff !important;
			border: 1px solid #be3437 !important;
		}

		@keyframes leadx-modal-fade-in {
			from {
				opacity: 0;
			}
			to {
				opacity: 1;
			}
		}

		@keyframes leadx-modal-scale-in {
			from {
				opacity: 0;
				transform: scale(0.96);
			}
			to {
				opacity: 1;
				transform: scale(1);
			}
		}

		@media (max-width: 520px) {
			.leadx-modal-panel {
				padding: 40px 28px;
			}

			.leadx-modal-button {
				height: 48px;
				font-size: 16px;
			}
		}
	`;
		document.head.appendChild(style);
	}

	// Return a function to close the modal programmatically
	return {
		close: closeModal,
	};
}

/**
 * Shows a modal for CRM connection requirement
 */
export function showCrmConnectionModal(crmProvider: ICRMProviderType) {
	const crmName = crmProviderNames[crmProviderTypes.findIndex((item) => item === crmProvider)];

	return showModal({
		title: `${crmName} Connection Required`,
		message: `You need to connect your ${crmName} account before exporting data.`,
		confirmText: "Connect Now",
		cancelText: "Cancel",
		onConfirm: () => {
			window.location.href = "/account/crm";
		},
	});
}

/**
 * Shows a modal recommending CRM connection
 */
export function showCrmRecommendationModal() {
	return showModal({
		title: "CRM Connection Recommended",
		message: "Connect a CRM integration to export companies and contacts directly.",
		confirmText: "Set Up Now",
		cancelText: "Later",
		onConfirm: () => {
			window.location.href = "/account/crm";
		},
	});
}
